import uniqueId from "lodash/uniqueId";

import React, { useCallback, useMemo, useReducer, useRef } from "react";

import useI18nNamespace from "@services/useI18nNamespace";

import { AsyncConfirmationModal } from "./AsyncConfirmationModal";
import { AsyncConfirmationModalContext } from "./AsyncConfirmationModalContext";
import { initialState, reducer } from "./reducer";
import type {
  AsyncConfirmationModalArgs,
  AsyncConfirmationModalContextType,
  AsyncConfirmationModalProviderProps,
  AsyncConfirmationModalStackItem,
  AsyncConfirmationResolver,
  AsyncConfirmResultType,
} from "./types";

export const AsyncConfirmationModalProvider = ({
  children,
}: AsyncConfirmationModalProviderProps): React.ReactElement => {
  const { t } = useI18nNamespace("async-confirmation-modal");

  const [state, dispatch] = useReducer(reducer, initialState);
  const resolvers = useRef<Record<string, AsyncConfirmationResolver>>({});

  const confirm = useCallback(
    (args: AsyncConfirmationModalArgs, openOnAdd = true): Promise<AsyncConfirmResultType> => {
      const id = uniqueId();

      const modal: AsyncConfirmationModalStackItem = {
        ...args,
        id,
        body: args.body ?? t("default-body", "Please confirm"),
        okText: args.okText ?? t("default-ok-text", "OK"),
        cancelText: args.cancelText ?? t("default-cancel-text", "Cancel"),
        size: args.size ?? "sm",
      };

      dispatch({ type: "OPEN_MODAL", modal, openOnAdd });

      return new Promise((resolve) => {
        resolvers.current[id] = resolve;
      });
    },
    [dispatch, t]
  );

  const handleOk = useCallback(
    (id: string) => () => {
      // resolve the promise
      resolvers.current[id]?.("ok");

      // update state in reducer
      dispatch({ type: "CLOSE_MODAL" });
    },
    []
  );

  const handleCancel = useCallback(
    (id: string) => () => {
      // resolve the promise
      resolvers.current[id]?.("cancel");

      // update state in reducer
      dispatch({ type: "CLOSE_MODAL" });
    },
    []
  );

  const handleTertiary = useCallback(
    (id: string) => () => {
      // resolve the promise
      resolvers.current[id]?.("tertiary");

      // update state in reducer
      dispatch({ type: "CLOSE_MODAL" });
    },
    []
  );

  const value = useMemo((): AsyncConfirmationModalContextType => {
    // Get the active modal from the stack
    const activeModal = state.stack.length > 0 ? state.stack[state.stack.length - 1] : null;

    // Return the context value
    return { state, activeModal, confirm, handleCancel, handleOk, handleTertiary };
  }, [state, confirm, handleCancel, handleOk, handleTertiary]);

  return (
    <AsyncConfirmationModalContext.Provider value={value}>
      {children}
      <AsyncConfirmationModal />
    </AsyncConfirmationModalContext.Provider>
  );
};
